import { AddressType, type SingleAddress } from '@zoocasa/go-search';
import type { ParsedAddressPath } from 'data/addresses/go-search/addressPathParser';
import {
  provinceAndStateSlugMappings,
  provinceCodeFromSlugMappings,
  provinceOrStateCodeFromString,
  stateCodeFromSlugMappings,
} from 'utils/province_or_state';

/**
 * `area_slug` for `/api/map/*` must align with listings_v2 keyword fields:
 * `neighbourhood_slug`, `sub_division_slug`, `province_or_state_slug` (server ORs them).
 * URL slugs like `milton-on` are not stored on those fields — use subdivision-only,
 * neighbourhood-only, or full province name slug (e.g. `ontario`), not `on`.
 */
export function getListingsV2MapAreaSlug(
  elected: SingleAddress,
  requested?: ParsedAddressPath,
): string | undefined {
  if (!elected.slug?.trim()) {
    return undefined;
  }

  switch (elected.addressType) {
    case AddressType.ADDRESS_TYPE_NEIGHBOURHOOD:
      return neighbourhoodSlugForListingsV2(elected, requested);

    case AddressType.ADDRESS_TYPE_SUB_DIVISION:
      return subdivisionSlugForListingsV2(elected, requested);

    case AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE:
      return normalizeProvinceSlugForListingsV2(
        requested?.provinceOrState?.slug ?? elected.slug,
      );

    default:
      return elected.slug.trim().toLowerCase();
  }
}

function neighbourhoodSlugForListingsV2(elected: SingleAddress, requested?: ParsedAddressPath): string {
  if (requested?.neighbourhood?.slug?.trim()) {
    return requested.neighbourhood.slug.trim().toLowerCase();
  }
  const subSlug = requested?.subDivision?.slug;
  if (subSlug && elected.slug) {
    const suffix = `-${subSlug}`;
    const s = elected.slug;
    if (s.toLowerCase().endsWith(suffix.toLowerCase())) {
      return s.slice(0, -suffix.length).trim().toLowerCase();
    }
  }
  return elected.slug.trim().toLowerCase();
}

function subdivisionSlugForListingsV2(elected: SingleAddress, requested?: ParsedAddressPath): string {
  const composite = (requested?.subDivision?.slug ?? elected.slug).trim().toLowerCase();
  const prov = requested?.provinceOrState?.slug?.trim().toLowerCase();
  if (prov && composite.endsWith(`-${prov}`)) {
    return composite.slice(0, -(prov.length + 1));
  }
  // URL often has no province segment in props; strip trailing -xx when xx is a valid province/state code
  // (e.g. toronto-on → toronto for sub_division_slug.keyword).
  return stripTrailingAdministrativeAreaCodeFromComposite(composite);
}

/** Drops a final `-{ca|on|ny|...}` segment when it matches a known province/state code. */
function stripTrailingAdministrativeAreaCodeFromComposite(composite: string): string {
  const segments = composite.split('-');
  if (segments.length < 2) {
    return composite;
  }
  const last = segments[segments.length - 1];
  if (last.length === 2 && provinceOrStateCodeFromString(last)) {
    return segments.slice(0, -1).join('-');
  }
  return composite;
}

function normalizeProvinceSlugForListingsV2(raw: string): string {
  const t = raw.trim().toLowerCase();
  if (provinceCodeFromSlugMappings[t] || stateCodeFromSlugMappings[t]) {
    return t;
  }
  const code = provinceOrStateCodeFromString(t);
  if (code) {
    const mapped = provinceAndStateSlugMappings[code];
    if (mapped) {
      return mapped;
    }
  }
  return t;
}
